import React, { useState } from 'react';
import { turkeyDate } from '../student360Rules';
import { Card } from '../components/ui';
import { BLUE_PRIMARY, BLUE_PRIMARY_DARK, BLUE_SOFT_SURFACE } from '../theme';

export default function Homeworks({ homeworks, stats, formatDate, today = turkeyDate() }) {
  const [limit, setLimit] = useState(5);
  const normalizeHomeworkStatus = (value) => {
    const v = String(value || '').toLowerCase();
    if (v === 'done') return 'done';
    if (v === 'missing') return 'missing';
    if (v === 'not_done') return 'not_done';
    return '';
  };

  const isHomeworkActive = (hw) => {
    if (!hw) return false;
    // Active: due today or later, or no due_date (teacher/admin defined without date)
    if (!hw.due_date) return true;
    return hw.due_date >= today;
  };

  const activeHomeworks = Array.isArray(homeworks)
    ? homeworks.filter(isHomeworkActive)
    : [];

  const homeworkStats = (() => {
    const backend = stats;
    if (backend && typeof backend.total === 'number') {
      return {
        total: backend.total || 0,
        done: backend.done_count || 0,
        missing: backend.missing_count || 0,
        notDone: backend.not_done_count || 0,
        donePct: backend.done_pct || 0,
        missingPct: backend.missing_pct || 0,
        notDonePct: backend.not_done_pct || 0,
      };
    }

    // Fallback for older backend: count only concluded homeworks (those with a status)
    const counts = { done: 0, missing: 0, notDone: 0 };
    const all = Array.isArray(homeworks) ? homeworks : [];
    for (const hw of all) {
      const st = normalizeHomeworkStatus(hw.status);
      if (st === 'done') counts.done += 1;
      else if (st === 'missing') counts.missing += 1;
      else if (st === 'not_done') counts.notDone += 1;
      // else: exclude items without status
    }
    const total = counts.done + counts.missing + counts.notDone;
    const pct = (n) => (total ? Math.round((n / total) * 1000) / 10 : 0);
    return {
      total,
      done: counts.done,
      missing: counts.missing,
      notDone: counts.notDone,
      donePct: pct(counts.done),
      missingPct: pct(counts.missing),
      notDonePct: pct(counts.notDone),
    };
  })();

  const hasStats = homeworkStats.total > 0;
  const hasActive = activeHomeworks.length > 0;

  if (!hasStats && !hasActive) return null;

  return (
    <>
      {hasStats && (
        <Card title="Ödev Durumları">
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
              <div style={{ background: '#fff', border: '1px solid #eef2f7', borderRadius: 12, padding: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 700 }}>Yapıldı</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{homeworkStats.donePct}%</div>
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{homeworkStats.done}</div>
              </div>
              <div style={{ background: '#fff', border: '1px solid #eef2f7', borderRadius: 12, padding: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <div style={{ fontSize: 12, color: BLUE_PRIMARY_DARK, fontWeight: 700 }}>Eksik</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{homeworkStats.missingPct}%</div>
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{homeworkStats.missing}</div>
              </div>
              <div style={{ background: '#fff', border: '1px solid #eef2f7', borderRadius: 12, padding: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>Yapılmadı</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{homeworkStats.notDonePct}%</div>
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{homeworkStats.notDone}</div>
              </div>
            </div>

            <div style={{ height: 10, borderRadius: 999, overflow: 'hidden', background: '#eef2f7', display: 'flex' }}>
              <div style={{ width: `${homeworkStats.donePct}%`, background: '#22c55e' }} />
              <div style={{ width: `${homeworkStats.missingPct}%`, background: BLUE_PRIMARY }} />
              <div style={{ width: `${homeworkStats.notDonePct}%`, background: '#94a3b8' }} />
            </div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Toplam ödev: <span style={{ fontWeight: 700, color: '#0f172a' }}>{homeworkStats.total}</span>
            </div>
          </div>
        </Card>
      )}

      {hasActive && (
        <Card title="Aktif Ödevler">
          {activeHomeworks.slice(0, limit).map((h, i) => {
            const st = normalizeHomeworkStatus(h.status);
            const label = st === 'done' ? 'Yapıldı' : (st === 'missing' ? 'Eksik' : 'Yapılmadı');
            const pill = st === 'done'
              ? { color: '#16a34a', borderColor: '#bbf7d0', background: '#f0fdf4' }
              : (st === 'missing'
                ? { color: BLUE_PRIMARY_DARK, borderColor: '#dbeafe', background: BLUE_SOFT_SURFACE }
                : { color: '#64748b', borderColor: '#e2e8f0', background: '#fff' });
            return (
              <div id={`homework-${h.homework_id}`} key={h.homework_id || i} style={{ padding: '10px 0', borderBottom: i === Math.min(activeHomeworks.length, limit) - 1 ? 'none' : '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                  <div style={{ fontWeight: 700 }}>{h.subject_label}</div>
                  {st && (
                    <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 999, border: `1px solid ${pill.borderColor}`, background: pill.background, color: pill.color }}>
                      {label}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 13, color: '#334155', marginTop: 4, whiteSpace: 'pre-wrap' }}>{h.description}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>Teslim: {h.due_date ? formatDate(h.due_date) : '-'}</div>
              </div>
            );
          })}

          {activeHomeworks.length > limit && (
            <button className="student360-action" onClick={() => setLimit((value) => value + 5)}>Daha fazla göster</button>
          )}
        </Card>
      )}
    </>
  );
}
